// bots.ts — offline AI opponents for solo mode. Pure logic (no Three.js) so
// it can be unit-tested; GameEngine owns rendering. Mirrors the Go arena bot
// (server/internal/room/bot.go): flee bigger holes, hunt smaller ones, else
// go for the most valuable object that fits.
import { canFitInHole, type ObjectDefinition } from "./catalog";

export const KILL_RATIO = 1.15;
/** Bots only chase prey that leaves a real capture window (full containment). */
export const HUNT_RATIO = 1.35;
export const MAX_RADIUS = 11.5;
export const START_RADIUS = 2.4;
export const RESPAWN_DELAY = 2.5;

export interface Contestant {
  id: number;
  name: string;
  x: number;
  z: number;
  radius: number;
  targetRadius: number;
  score: number;
  eaten: number;
  alive: boolean;
  respawnAt: number;
}

export interface Bot extends Contestant {
  skill: number;
  tx: number;
  tz: number;
  nextPlan: number;
  wanderX: number;
  wanderZ: number;
  color: string;
}

export interface BotItem {
  id: number;
  x: number;
  z: number;
  definition: ObjectDefinition;
}

export const BOT_NAMES = [
  "Gulpy", "Void", "Nom Nom", "Abyss", "Crater", "Vortex", "Muncher", "Sinkhole",
  "Black Pearl", "Pit Boss", "Chomper", "Eclipse", "Dr. Hole", "Orbit", "Hungry Hank",
];
export const BOT_COLORS = ["#ff5f7e", "#ffb347", "#7ce38b", "#62c6ff", "#c58bff", "#ffd761", "#ff8bd8", "#4de6d0"];

export function holeSpeed(radius: number) {
  return Math.max(4.5, 12.5 / (1 + radius * 0.035));
}

export function createBots(count: number, rand: () => number, rail: number): Bot[] {
  const names = [...BOT_NAMES].sort(() => rand() - 0.5);
  const bots: Bot[] = [];
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2 + rand() * 0.4;
    const dist = rail * (0.45 + rand() * 0.4);
    const x = Math.cos(angle) * dist;
    const z = Math.sin(angle) * dist;
    bots.push({
      id: i + 1,
      name: names[i % names.length]!,
      x, z, tx: x, tz: z,
      radius: START_RADIUS,
      targetRadius: START_RADIUS,
      score: 0,
      eaten: 0,
      alive: true,
      respawnAt: 0,
      // Spread skills: some easy prey, at least one real threat.
      skill: Math.min(1, 0.15 + (i % 4) * 0.27 + rand() * 0.1),
      nextPlan: 0,
      wanderX: (rand() * 2 - 1) * rail,
      wanderZ: (rand() * 2 - 1) * rail,
      color: BOT_COLORS[i % BOT_COLORS.length]!,
    });
  }
  return bots;
}

/** Re-plan the bot's movement target. */
export function planBot(bot: Bot, others: readonly Contestant[], items: readonly BotItem[], rand: () => number, rail: number) {
  const vision = 16 + bot.radius * 3 + bot.skill * 10;

  let fleeX = 0;
  let fleeZ = 0;
  let threatened = false;
  for (const o of others) {
    if (o === bot || !o.alive || o.radius <= bot.radius * KILL_RATIO) continue;
    const dx = bot.x - o.x;
    const dz = bot.z - o.z;
    const d = Math.hypot(dx, dz);
    const danger = vision * 0.8 + o.radius;
    if (d < danger && d > 0.01) {
      const w = (danger - d) / danger;
      fleeX += (dx / d) * w;
      fleeZ += (dz / d) * w;
      threatened = true;
    }
  }
  if (threatened && rand() < 0.55 + bot.skill * 0.45) {
    fleeX -= (bot.x / rail) * 0.35;
    fleeZ -= (bot.z / rail) * 0.35;
    setTarget(bot, bot.x + fleeX * 20, bot.z + fleeZ * 20, rail);
    return;
  }

  let prey: Contestant | undefined;
  let preyD = vision;
  for (const o of others) {
    if (o === bot || !o.alive || bot.radius <= o.radius * HUNT_RATIO) continue;
    const d = Math.hypot(bot.x - o.x, bot.z - o.z);
    if (d < preyD) { prey = o; preyD = d; }
  }
  if (prey && rand() < 0.25 + bot.skill * 0.6) {
    setTarget(bot, prey.x, prey.z, rail);
    return;
  }

  let best: BotItem | undefined;
  let bestValue = 0;
  for (const it of items) {
    if (!canFitInHole(bot.radius, it.definition.footprint)) continue;
    const d = Math.hypot(bot.x - it.x, bot.z - it.z);
    if (d > vision * 1.6) continue;
    const value = it.definition.score / (d + 4);
    if (value > bestValue) { best = it; bestValue = value; }
  }
  if (best) {
    setTarget(bot, best.x, best.z, rail);
    return;
  }

  if (Math.hypot(bot.x - bot.wanderX, bot.z - bot.wanderZ) < 3) {
    bot.wanderX = (rand() * 2 - 1) * rail;
    bot.wanderZ = (rand() * 2 - 1) * rail;
  }
  setTarget(bot, bot.wanderX, bot.wanderZ, rail);
}

function setTarget(bot: Bot, x: number, z: number, rail: number) {
  bot.tx = Math.max(-rail, Math.min(rail, x));
  bot.tz = Math.max(-rail, Math.min(rail, z));
}

export function moveBot(bot: Bot, dt: number) {
  const dx = bot.tx - bot.x;
  const dz = bot.tz - bot.z;
  const d = Math.hypot(dx, dz);
  if (d < 0.08) return;
  // Weaker bots are a touch slower so the player can outrun them.
  const step = Math.min(holeSpeed(bot.radius) * (0.86 + bot.skill * 0.14) * dt, d);
  bot.x += (dx / d) * step;
  bot.z += (dz / d) * step;
}

/**
 * Returns [winner, loser] when one hole swallows the other, else undefined.
 *
 * Rule: the smaller hole must be *completely* inside the bigger one
 * (center distance + small radius <= big radius). Merely touching is not
 * enough — the victim has to be engulfed.
 */
export function duel(a: Contestant, b: Contestant): [Contestant, Contestant] | undefined {
  if (!a.alive || !b.alive) return undefined;
  const [big, small] = a.radius >= b.radius ? [a, b] : [b, a];
  if (big.radius <= small.radius * KILL_RATIO) return undefined;
  const d = Math.hypot(a.x - b.x, a.z - b.z);
  if (d + small.radius > big.radius) return undefined;
  return [big, small];
}

export function applyKill(winner: Contestant, loser: Contestant, now: number, keepLoserScore: boolean) {
  winner.score += Math.floor(loser.score * 0.5) + 150;
  winner.targetRadius = Math.min(MAX_RADIUS, winner.targetRadius + loser.radius * 0.3);
  loser.alive = false;
  loser.respawnAt = now + RESPAWN_DELAY;
  loser.radius = loser.targetRadius = Math.max(START_RADIUS, loser.radius * 0.6);
  if (!keepLoserScore) loser.score = Math.floor(loser.score * 0.5);
}

export interface LeaderEntry {
  name: string;
  score: number;
  /** Hole diameter in world units; the leaderboard ranks by this. */
  size: number;
  me: boolean;
  color?: string;
}

/**
 * Rank by hole size, not by points.
 *
 * Size is what decides who can swallow whom, so ranking by score made the
 * board look wrong (“that bot has fewer points but is bigger”). Points stay
 * visible as a secondary number, but the order always matches the actual
 * hole sizes on the map.
 */
export function leaderboard(entries: LeaderEntry[]): LeaderEntry[] {
  return [...entries].sort((a, b) => b.size - a.size || b.score - a.score);
}
